import { __assign, __extends, __rest } from "tslib";
import { get, isString, compact } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY } from '../../constants/messages';
import { MAX_PINTEREST_IMAGE_SIZE } from '../../services/viewer';
import { getTextColorSchema, styleSelectionPredicate, viewerCustomStyleFn } from '../../services/text-color-utils';
import { createHashtagHref, createHashtagPath } from '../../services/search-page-url';
import { isIos } from '../../../services/detect-platform';
import getTheme from '../../services/theme';
import getStyle from '../../services/get-style';
import Wix from '../../../services/wix-sdk-polyfill';
import styles from './rich-content-viewer.scss';
var anchorTarget = '_top';
var RichContentViewer = /** @class */ (function (_super) {
    __extends(RichContentViewer, _super);
    function RichContentViewer(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            error: false,
            isViewerDisabled: false,
            isExpandMode: false,
            expandModeImageIndex: undefined,
        };
        _this.enableViewer = function () { return _this.setState({ isViewerDisabled: false }); };
        _this.disableViewer = function () { return _this.setState({ isViewerDisabled: true }); };
        _this.getVideoPluginConfig = function () { return ({ getVideoUrl: function (src) { return _this.props.getVideoUrl(src.pathname); } }); };
        _this.onHashTagClick = function (event) {
            event.preventDefault();
            event.stopPropagation();
            _this.props.hashtagNavigate(createHashtagPath(event.target.innerText));
        };
        _this.onExpandImage = function (entityIndex, innerIndex) {
            if (innerIndex === void 0) { innerIndex = 0; }
            _this.setState({
                isExpandMode: true,
                expandModeImageIndex: _this.getExpandModeImages().imageMap[entityIndex] + innerIndex,
            });
        };
        _this.getExpandModeImages = function () {
            if (!_this.imagesData) {
                _this.imagesData = _this.rce.getImagesData(_this.props.initialRawState);
                _this.imagesData.images.forEach(function (img) {
                    if (!img.itemId) {
                        img.itemId = img.url;
                    }
                });
            }
            return _this.imagesData;
        };
        _this.onError = function (error) {
            if (!_this.state.error) {
                _this.setState({ error: error });
            }
        };
        _this.initViewerProps();
        _this.Viewer = props.richContent.RichContentViewer;
        return _this;
    }
    RichContentViewer.prototype.componentDidMount = function () {
        Wix.addEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.enableViewer);
        Wix.addEventListener(Wix.Events.PAGE_NAVIGATION_OUT, this.disableViewer);
    };
    RichContentViewer.prototype.componentWillUnmount = function () {
        Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.enableViewer);
        Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION_OUT, this.disableViewer);
    };
    RichContentViewer.prototype.componentDidUpdate = function () {
        if (this.props.isInEditor()) {
            this.initTheme();
        }
    };
    RichContentViewer.prototype.getImagePluginConfig = function () {
        var _this = this;
        return {
            imageProps: function (src) { return ({
                'data-pin-url': _this.props.pageUrl,
                'data-pin-media': isString(src)
                    ? src
                    : _this.props.getImageUrl(src, MAX_PINTEREST_IMAGE_SIZE, MAX_PINTEREST_IMAGE_SIZE),
            }); },
        };
    };
    RichContentViewer.prototype.initViewerProps = function () {
        var _a;
        var _this = this;
        var _b = this.props, enableLinks = _b.enableLinks, isOneApp = _b.isOneApp, isOOI = _b.isOOI, isSSR = _b.isSSR, appSettings = _b.appSettings, richContent = _b.richContent, htmlIframeSrc = _b.htmlIframeSrc, isInEditor = _b.isInEditor, isInPreview = _b.isInPreview, postId = _b.postId, actions = _b.actions, bi = _b.bi;
        this.rce = richContent;
        var _c = this.rce, giphyTypeMapper = _c.giphyTypeMapper, videoTypeMapper = _c.videoTypeMapper, imageTypeMapper = _c.imageTypeMapper, galleryTypeMapper = _c.galleryTypeMapper, dividerTypeMapper = _c.dividerTypeMapper, htmlTypeMapper = _c.htmlTypeMapper, linkTypeMapper = _c.linkTypeMapper, fileUploadTypeMapper = _c.fileUploadTypeMapper, buttonTypeMapper = _c.buttonTypeMapper, TEXT_COLOR_TYPE = _c.TEXT_COLOR_TYPE, HTML_TYPE = _c.HTML_TYPE, IMAGE_TYPE = _c.IMAGE_TYPE, VIDEO_TYPE = _c.VIDEO_TYPE, GALLERY_TYPE = _c.GALLERY_TYPE, LINK_TYPE = _c.LINK_TYPE, FILE_UPLOAD_TYPE = _c.FILE_UPLOAD_TYPE, ACTION_BUTTON_TYPE = _c.ACTION_BUTTON_TYPE;
        this.typeMappers = compact([
            videoTypeMapper,
            imageTypeMapper,
            galleryTypeMapper,
            dividerTypeMapper,
            htmlTypeMapper,
            giphyTypeMapper,
            enableLinks && linkTypeMapper,
            fileUploadTypeMapper,
            buttonTypeMapper,
        ]);
        if (enableLinks && isOneApp) {
            this.oneAppLinkConfig = {
                onClick: function (event, url) {
                    event.preventDefault();
                    _this.props.actions.openUrl({ url: url });
                },
            };
        }
        this.helpers = {};
        this.initTheme();
        var isExpandEnabled = this.props.fullscreenComponent && !(isInEditor() || isInPreview());
        if (isExpandEnabled) {
            this.helpers.onExpand = this.onExpandImage;
        }
        var colorScheme = getTextColorSchema(appSettings);
        this.config = (_a = {},
            _a[FILE_UPLOAD_TYPE] = {
                resolveFileUrl: function (_a) {
                    var config = _a.config, file = __rest(_a, ["config"]);
                    isIos() && _this.props.actions.showMessage(RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY);
                    var getBiProps = function (success) { return ({
                        file_ext: file.type,
                        file_id: file.id,
                        file_size: file.size,
                        mime_type: file.mimeType,
                        post_id: postId,
                        origin: 'post',
                        success: success,
                    }); };
                    return actions
                        .requestFileDownloadUrl(postId, file.path, file.id, file.mimeType)
                        .then(function (_a) {
                        var url = _a.url;
                        bi.fileDownloaded(getBiProps(true));
                        return url;
                    })
                        .catch(function () { return bi.fileDownloaded(getBiProps(false)); });
                },
            },
            _a[TEXT_COLOR_TYPE] = {
                customStyleFn: viewerCustomStyleFn(colorScheme, richContent),
                styleSelectionPredicate: styleSelectionPredicate(colorScheme, richContent),
            },
            _a[HTML_TYPE] = { htmlIframeSrc: htmlIframeSrc },
            _a[IMAGE_TYPE] = this.getImagePluginConfig(),
            _a[VIDEO_TYPE] = this.getVideoPluginConfig(),
            _a[GALLERY_TYPE] = {
                imageProps: function () { return ({ 'data-pin-url': _this.props.pageUrl }); },
            },
            _a[LINK_TYPE] = this.oneAppLinkConfig,
            _a[ACTION_BUTTON_TYPE] = {
                onClick: this.props.onButtonClick,
            },
            _a);
        var useCustomScroll = !isOOI || (isOOI && (isInEditor() || isInPreview()));
        var scrollingElement = useCustomScroll
            ? function () { return ({
                addEventListener: function (eventName, callback) {
                    Wix.addEventListener(eventName.toUpperCase(), callback);
                },
                removeEventListener: function (eventName, callback) {
                    Wix.removeEventListener(eventName.toUpperCase(), callback);
                },
            }); }
            : !isSSR && this.rce.RCE_VERSION !== '6.10.5' && window;
        if (this.config[GALLERY_TYPE]) {
            this.config[GALLERY_TYPE].scrollingElement = scrollingElement;
        }
        this.initDecorators();
        this.initInlineStyleMappers(this.config);
    };
    RichContentViewer.prototype.initTheme = function () {
        var _a = this.props, appSettings = _a.appSettings, richContentFontClassName = _a.richContentFontClassName, useDescriptionFontInEditor = _a.useDescriptionFontInEditor, isPostPageEnabled = _a.isPostPageEnabled, isCompact = _a.isCompact, isSeo = _a.isSeo, isMobile = _a.isMobile, emotion = _a.emotion, isPostPageBundle = _a.isPostPageBundle, isOOI = _a.isOOI;
        var isPostPage = isPostPageBundle();
        this.theme = this.props.disableTheme
            ? {}
            : this.props.getTheme({
                appSettings: appSettings,
                readOnly: true,
                isCompact: isCompact,
                isViewer: true,
                isSeo: isSeo,
                isOOI: isOOI,
                isMobile: isMobile,
                isPostPageEnabled: isPostPageEnabled,
                richContentFontClassName: richContentFontClassName,
                useDescriptionFontInEditor: useDescriptionFontInEditor && isPostPageEnabled,
                isPostPage: isPostPage,
                css: emotion.css,
                version: this.rce.RCE_VERSION,
            });
        this.style = this.props.getStyle(appSettings, { isPostPageEnabled: isPostPageEnabled });
    };
    RichContentViewer.prototype.initDecorators = function () {
        var _a;
        var _this = this;
        var _b = this.rce, HashtagDecorator = _b.HashtagDecorator, HEADERS_MARKDOWN_TYPE = _b.HEADERS_MARKDOWN_TYPE, createHeadersMarkdownDecorator = _b.createHeadersMarkdownDecorator, CodeBlockDecorator = _b.CodeBlockDecorator;
        this.decorators = compact([
            new HashtagDecorator({
                theme: this.theme,
                onClick: this.onHashTagClick,
                createHref: function (decoratedText) { return createHashtagHref(decoratedText, _this.props.sectionUrl); },
            }),
            createHeadersMarkdownDecorator((_a = {},
                _a[HEADERS_MARKDOWN_TYPE] = {
                    hideMarkdown: true,
                },
                _a)),
            CodeBlockDecorator && new CodeBlockDecorator({ theme: this.theme }),
        ]);
    };
    RichContentViewer.prototype.initInlineStyleMappers = function (config) {
        this.inlineStyleMappers = [this.rce.textColorInlineStyleMapper(config, this.props.initialRawState)];
    };
    RichContentViewer.getDerivedStateFromError = function (error) {
        return { error: error };
    };
    RichContentViewer.prototype.render = function () {
        var _this = this;
        var _a = this.state, error = _a.error, isExpandMode = _a.isExpandMode, expandModeImageIndex = _a.expandModeImageIndex;
        if (error) {
            return this.props.fallback();
        }
        var _b = this.props, initialRawState = _b.initialRawState, viewerKey = _b.viewerKey, relValue = _b.relValue, isMobile = _b.isMobile, isInEditor = _b.isInEditor, isSeo = _b.isSeo, isSSR = _b.isSSR, Fullscreen = _b.fullscreenComponent;
        var _c = this.theme, _d = _c.advancedPostOpts, advancedPostOpts = _d === void 0 ? '' : _d, blogSharedValues = _c.blogSharedValues;
        if (isInEditor()) {
            this.initTheme();
        }
        var Viewer = this.Viewer;
        return (React.createElement("div", { style: this.style, className: classNames(styles.container, this.theme.overrideDefaultTheme, this.theme.overrideAppSettings), "data-rce-version": this.props.richContent.RCE_VERSION },
            React.createElement(Viewer, __assign({}, this.props, { key: "" + advancedPostOpts + get(blogSharedValues, 'colors.linkHashtagColor', '') + viewerKey, typeMappers: this.typeMappers, ref: function (ref) { return (_this.editor = ref); }, initialState: initialRawState, helpers: this.helpers, decorators: this.decorators, seoMode: isSeo, theme: this.theme, platform: isMobile ? 'mobile' : 'desktop', anchorTarget: anchorTarget, relValue: relValue, config: this.config, inlineStyleMappers: this.inlineStyleMappers, disabled: this.state.isViewerDisabled, onError: this.onError })),
            !isSSR && Fullscreen && isExpandMode && (React.createElement(Fullscreen, { isOpen: isExpandMode, images: this.getExpandModeImages().images, onClose: function () { return _this.setState({ isExpandMode: false }); }, index: expandModeImageIndex }))));
    };
    return RichContentViewer;
}(Component));
RichContentViewer.propTypes = {
    actions: PropTypes.exact({
        openUrl: PropTypes.func.isRequired,
        hashtagNavigate: PropTypes.func.isRequired,
    }),
    initialRawState: PropTypes.object,
    isCompact: PropTypes.bool,
    appSettings: PropTypes.object.isRequired,
    pageUrl: PropTypes.string.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isOneApp: PropTypes.bool.isRequired,
    enableLinks: PropTypes.bool.isRequired,
    isPostPageEnabled: PropTypes.bool.isRequired,
    richContentFontClassName: PropTypes.string.isRequired,
    useDescriptionFontInEditor: PropTypes.bool,
    isSeo: PropTypes.bool,
    viewerKey: PropTypes.string.isRequired,
    emotion: PropTypes.object.isRequired,
    relValue: PropTypes.string,
    postId: PropTypes.string,
    getVideoUrl: PropTypes.func.isRequired,
    getImageUrl: PropTypes.func.isRequired,
    getTheme: PropTypes.func,
    getStyle: PropTypes.func,
    isInEditor: PropTypes.func.isRequired,
    isPostPageBundle: PropTypes.func.isRequired,
    htmlIframeSrc: PropTypes.string.isRequired,
    richContent: PropTypes.object.isRequired,
    fallback: PropTypes.any,
    onButtonClick: PropTypes.func,
    sectionUrl: PropTypes.string,
    disableTheme: PropTypes.bool,
};
RichContentViewer.defaultProps = {
    isCompact: false,
    useDescriptionFontInEditor: true,
    relValue: 'noreferrer',
    fallback: function () { return 'Content cannot be displayed'; },
    getTheme: getTheme,
    getStyle: getStyle,
};
export default RichContentViewer;
